// Types.js - Same UI as Brands/Variations (City removed + Category removed) + 10 per page pagination

import React, { useState, useEffect, useRef } from 'react';
import {
  FaTags,
  FaSearch,
  FaPlus,
  FaEdit,
  FaCheckDouble,
  FaStore,
  FaEllipsisV,
  FaChevronRight
} from 'react-icons/fa';
import { TbSitemap } from 'react-icons/tb';
import { RiDeleteBin5Line, RiDeleteBinLine } from "react-icons/ri";
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import TablePagination from '../../components/TablePagination';
import './Types.css';

// Har page par sirf itni rows
const PAGE_SIZE = 10;

// Last loaded types of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const TYPES_CACHE_PREFIX = 'types_cache_';

const readTypesCache = (id) => {
  if (!id) return null;
  try {
    const raw = localStorage.getItem(`${TYPES_CACHE_PREFIX}${id}`);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeTypesCache = (id, data) => {
  if (!id) return;
  try {
    localStorage.setItem(`${TYPES_CACHE_PREFIX}${id}`, JSON.stringify(data));
  } catch (e) {}
};

const Types = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [typeName, setTypeName] = useState('');
  const [shopName, setShopName] = useState('');
  const [selectedShopId, setSelectedShopId] = useState('');
  const [showActionDropdown, setShowActionDropdown] = useState(false);

  // Move To (copy to other shops)
  const [showMoveList, setShowMoveList] = useState(false);
  const [moveShopIds, setMoveShopIds] = useState([]);
  const [moveLoading, setMoveLoading] = useState(false);

  // NAYA STATE: Save process ko track karne ke liye
  const [isSaving, setIsSaving] = useState(false);

  const [types, setTypes] = useState(() => readTypesCache(shopId) || []);
  // True only while there is nothing to show yet (so "No types found" never flashes)
  const [isLoading, setIsLoading] = useState(() => !readTypesCache(shopId));
  const [shops, setShops] = useState([]);

  const typesRequestRef = useRef(0);

  const API_BASE_URL = 'https://super-backend.hira-tech.co.uk/api/product';

  const showSuccessAlert = (message) => {
    Swal.fire({ icon: 'success', title: 'Success!', text: message, timer: 2000, showConfirmButton: false });
  };
  const showErrorAlert = (message) => {
    Swal.fire({ icon: 'error', title: 'Error!', text: message });
  };
  const showWarningAlert = (message) => {
    Swal.fire({ icon: 'warning', title: 'Warning!', text: message });
  };
  const showDeleteConfirmation = (typeName, isMultiple = false, count = 1) => {
    const title = isMultiple
      ? `Are you sure you want to delete ${count} type(s)?`
      : `Are you sure you want to delete type "${typeName}"?`;
    return Swal.fire({
      title: 'Are you sure?', text: title, icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6', confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });
  };

  const normalizeTypesResponse = (data) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    if (data && Array.isArray(data.data)) return data.data;
    return [];
  };

  const fetchShops = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/shops/`, { cache: 'no-store' });
      if (response.ok) {
        const data = await response.json();
        const shopList = Array.isArray(data) ? data : (data.results || data.data || []);
        setShops(shopList);
        return shopList;
      }
      return [];
    } catch (error) { return []; }
  };

  const fetchTypes = async (targetShopId) => {
    if (!targetShopId) return;

    // Only the latest request is allowed to update the screen
    const requestId = ++typesRequestRef.current;

    try {
      let url = `${API_BASE_URL}/types/?shop_id=${targetShopId}`;
      url += `&_t=${Date.now()}`;
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (requestId !== typesRequestRef.current) return;
      if (!response.ok) { setTypes([]); return; }

      const data = await response.json();
      if (requestId !== typesRequestRef.current) return;

      const typeList = normalizeTypesResponse(data);
      setTypes(typeList);
      writeTypesCache(targetShopId, typeList);

      if (typeList.length > 0 && typeList[0].shop) {
        setShopName(typeList[0].shop.shop_name || '');
      } else {
        try {
          const shopResponse = await fetch(`${API_BASE_URL}/shops/${targetShopId}/`, { cache: 'no-store' });
          if (shopResponse.ok) {
            const shopData = await shopResponse.json();
            setShopName(shopData.shop_name || '');
          }
        } catch (err) {}
      }
    } catch (error) {
      if (requestId === typesRequestRef.current) setTypes([]);
    } finally {
      if (requestId === typesRequestRef.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const shopsData = await fetchShops();
      if (!shopId && shopsData.length > 0) {
        navigate(`/shop/${shopsData[0].shop_id}/types`, { replace: true });
      } else if (!shopId) {
        setIsLoading(false);
      }
    };
    initialize();
  }, []);

  useEffect(() => {
    if (shopId) {
      const shopIdInt = parseInt(shopId, 10);

      // Show the saved list of this shop immediately (if there is one)
      const cached = readTypesCache(shopIdInt);
      if (cached) {
        setTypes(cached);
        setIsLoading(false);
      } else {
        setTypes([]);
        setIsLoading(true);
      }

      // Shop badalne par pehle page par
      setPage(1);

      fetchTypes(shopIdInt);
    }
  }, [shopId]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const filteredTypes = types.filter(
    (type) => type.type_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ✅ 10 per page
  const totalPages = Math.max(1, Math.ceil(filteredTypes.length / PAGE_SIZE));
  const paginatedTypes = filteredTypes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Delete / search se pages kam ho jayein to page number bahar na reh jaye
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // All shops except the one currently being viewed
  const otherShops = shops.filter((s) => String(s.shop_id) !== String(shopId));

  const resetMoveState = () => {
    setShowMoveList(false);
    setMoveShopIds([]);
  };

  const handleSelectAll = () => {
    if (selectedTypes.length === filteredTypes.length && filteredTypes.length > 0) {
      setSelectedTypes([]);
    } else {
      setSelectedTypes(filteredTypes.map((type) => type.type_id));
    }
    setShowActionDropdown(false);
    resetMoveState();
  };

  const handleSelectType = (typeId) => {
    if (selectedTypes.includes(typeId)) {
      setSelectedTypes(selectedTypes.filter((id) => id !== typeId));
    } else {
      setSelectedTypes([...selectedTypes, typeId]);
    }
    setShowActionDropdown(false);
    resetMoveState();
  };

  const handleToggleMoveShop = (id) => {
    const key = String(id);
    if (moveShopIds.includes(key)) {
      setMoveShopIds(moveShopIds.filter((x) => x !== key));
    } else {
      setMoveShopIds([...moveShopIds, key]);
    }
  };

  const handleMoveSelected = async () => {
    if (selectedTypes.length === 0) {
      showWarningAlert('Please select types first');
      return;
    }
    if (moveShopIds.length === 0) {
      showWarningAlert('Please select at least one shop');
      return;
    }

    setMoveLoading(true);

    const typesToCopy = types.filter((t) => selectedTypes.includes(t.type_id));
    let successCount = 0;
    let failedCount = 0;

    try {
      for (const targetId of moveShopIds) {
        for (const type of typesToCopy) {
          try {
            const response = await fetch(`${API_BASE_URL}/types/create/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({
                type_name: type.type_name,
                shop_id: parseInt(targetId, 10)
              })
            });
            if (response.ok) successCount++;
            else failedCount++;
          } catch (err) {
            failedCount++;
          }
        }
      }

      if (successCount > 0 && failedCount === 0) {
        showSuccessAlert(`${successCount} type(s) copied successfully!`);
      } else if (successCount > 0) {
        showWarningAlert(`${successCount} copied, ${failedCount} failed (maybe already exist).`);
      } else {
        showErrorAlert('Could not copy types (maybe they already exist in those shops).');
      }

      setSelectedTypes([]);
      setShowActionDropdown(false);
      resetMoveState();
      await fetchTypes(parseInt(shopId, 10));
    } finally {
      setMoveLoading(false);
    }
  };

  const handleDeleteSingleType = async (type) => {
    const result = await showDeleteConfirmation(type.type_name);
    if (!result.isConfirmed) return;
    try {
      const response = await fetch(`${API_BASE_URL}/types/${type.type_id}/delete/`, { method: 'DELETE' });
      if (response.ok || response.status === 204) {
        showSuccessAlert('Type deleted successfully!');
        setSelectedTypes(selectedTypes.filter((id) => id !== type.type_id));
        await fetchTypes(parseInt(shopId, 10));
      } else {
        showErrorAlert('Failed to delete type');
      }
    } catch (error) { showErrorAlert('Network error. Please try again.'); }
  };

  const handleDeleteSelected = async () => {
    if (selectedTypes.length === 0) {
      showWarningAlert('Please select types to delete');
      return;
    }
    const result = await showDeleteConfirmation(null, true, selectedTypes.length);
    if (!result.isConfirmed) return;
    try {
      let deletedCount = 0;
      for (const typeId of selectedTypes) {
        const response = await fetch(`${API_BASE_URL}/types/${typeId}/delete/`, { method: 'DELETE' });
        if (response.ok || response.status === 204) deletedCount++;
      }
      showSuccessAlert(`${deletedCount} type(s) deleted successfully!`);
      setSelectedTypes([]);
      await fetchTypes(parseInt(shopId, 10));
    } catch (error) { showErrorAlert('Failed to delete some types.'); }
  };

  const handleClearAll = () => { setSelectedTypes([]); };

  const handleOpenModalForNew = () => {
    setEditingType(null);
    setTypeName('');
    setSelectedShopId(shopId || '');
    setIsModalOpen(true);
  };

  const handleOpenModalForEdit = (type) => {
    setEditingType(type);
    setTypeName(type.type_name || '');
    const shopIdFromType = type.shop?.shop_id || shopId;
    setSelectedShopId(shopIdFromType);
    setIsModalOpen(true);
  };

  const handleModalShopChange = async (e) => {
    const newShopId = e.target.value;
    setSelectedShopId(newShopId);
  };

  // UPDATED HANDLE SAVE FUNCTION
  const handleSave = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }

    // 1. Agar pehle se saving ho rahi hai to kuch na karein
    if (isSaving) return;

    if (!typeName.trim()) { showWarningAlert('Please enter Type Name'); return; }
    const targetShopId = selectedShopId || shopId;
    if (!targetShopId) { showWarningAlert('Please select a Shop'); return; }
    const numericShopId = parseInt(targetShopId, 10);
    if (Number.isNaN(numericShopId)) { showErrorAlert('Invalid Shop ID'); return; }

    // 2. Saving state ko true karein (button disable ho jayega)
    setIsSaving(true);

    try {
      const url = editingType
        ? `${API_BASE_URL}/types/${editingType.type_id}/update/`
        : `${API_BASE_URL}/types/create/`;
      const payload = {
        type_name: typeName.trim(),
        shop_id: numericShopId
      };
      const response = await fetch(url, {
        method: editingType ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      const responseText = await response.text();
      let responseData = null;
      if (responseText) {
        try { responseData = JSON.parse(responseText); } catch (parseError) {}
      }

      if (response.ok) {
        if (responseData && responseData.type_id) {
          setTypes((previousTypes) => {
            if (editingType) {
              return previousTypes.map((existingType) =>
                existingType.type_id === responseData.type_id ? responseData : existingType
              );
            }
            return [...previousTypes, responseData];
          });
        }
        setIsModalOpen(false);
        setEditingType(null);
        setTypeName('');
        await fetchTypes(numericShopId);
        showSuccessAlert(editingType ? 'Type updated successfully!' : 'Type added successfully!');
        return; // Return ke baad finally block chalega
      }

      const backendError = responseData?.error || responseData?.detail || responseData?.message || 'Failed to save type';
      showErrorAlert(backendError);
    } catch (error) {
      showErrorAlert('An error occurred. Please check the browser console.');
    } finally {
      // 3. Request complete hone par (success ya error) saving state false karein
      setIsSaving(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingType(null);
    setTypeName('');
  };

  const getShopDisplayName = (type) => {
    if (type.shop) return `${type.shop.shop_name} (ID: ${type.shop.shop_id})`;
    if (type.shop_name) return `${type.shop_name}`;
    return 'No Shop';
  };

  return (
    <div className="types-main-container">

      {/* Heading + Search + Buttons */}
      <div className="types-action-section">
        <div className="types-heading">
          <h2>
            <TbSitemap className="types-heading-icon" />
            Types Management
          </h2>
        </div>

        <div className="types-action-right">
          <div className="types-search-wrapper">
            <FaSearch className="types-search-icon" />
            <input
              type="text"
              placeholder="Search by Type Name..."
              value={searchTerm}
              onChange={handleSearch}
              className="types-search-input"
            />
          </div>

          <div className="types-action-buttons">
            <button
              type="button"
              className="types-select-all-btn"
              onClick={handleSelectAll}
              disabled={moveLoading}
            >
              <FaCheckDouble />
              <span>
                {selectedTypes.length === filteredTypes.length && filteredTypes.length > 0
                  ? 'Deselect All'
                  : 'Select All'}
              </span>
            </button>

            <button
              type="button"
              className="types-add-btn"
              onClick={handleOpenModalForNew}
              disabled={moveLoading}
            >
              <FaPlus />
              <span>ADD TYPE</span>
            </button>
          </div>
        </div>
      </div>

      {selectedTypes.length > 0 && (
        <div className="selected-summary">
          <div className="summary-content">
            <span className="summary-icon">✓</span>
            <span className="summary-text">
              {selectedTypes.length} type(s) selected
            </span>
            <div className="action-dropdown-container">
              <button
                type="button"
                className="action-dropdown-btn"
                onClick={() => {
                  setShowActionDropdown(!showActionDropdown);
                  resetMoveState();
                }}
              >
                <FaEllipsisV />
                <span>Actions</span>
              </button>
              {showActionDropdown && (
                <div className="action-dropdown-menu">
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => setShowMoveList(!showMoveList)}
                  >
                    <FaChevronRight className={`move-arrow ${showMoveList ? 'move-arrow-open' : ''}`} />
                    <span>Move To</span>
                  </button>

                  {showMoveList && (
                    <div className="move-shops-panel">
                      <div className="move-shops-list">
                        {otherShops.length > 0 ? (
                          otherShops.map((shop) => (
                            <label key={shop.shop_id} className="move-shop-item">
                              <input
                                type="checkbox"
                                checked={moveShopIds.includes(String(shop.shop_id))}
                                onChange={() => handleToggleMoveShop(shop.shop_id)}
                                disabled={moveLoading}
                              />
                              <span>{shop.shop_name}</span>
                            </label>
                          ))
                        ) : (
                          <div className="move-no-shops">No other shops</div>
                        )}
                      </div>
                      {otherShops.length > 0 && (
                        <button
                          type="button"
                          className="move-apply-btn"
                          onClick={handleMoveSelected}
                          disabled={moveLoading || moveShopIds.length === 0}
                        >
                          {moveLoading ? 'Copying...' : `Copy to ${moveShopIds.length} shop(s)`}
                        </button>
                      )}
                    </div>
                  )}

                  <button
                    type="button"
                    className="dropdown-item delete-item"
                    onClick={handleDeleteSelected}
                  >
                    <RiDeleteBinLine />
                    <span>Delete Selected ({selectedTypes.length})</span>
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              className="clear-selection"
              onClick={() => { setSelectedTypes([]); setShowActionDropdown(false); resetMoveState(); }}
            >
              Clear all
            </button>
          </div>
        </div>
      )}

      {/* TABLE - Category column removed */}
      <div className="types-table-container">
        <div className="types-table-wrapper">
          <table className="types-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>
                  <input
                    type="checkbox"
                    checked={selectedTypes.length === filteredTypes.length && filteredTypes.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>TYPE NAME</th>
                <th>SHOP</th>
                <th style={{ width: '100px' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {/* Yahan se sirf loading aur no-data ka block delete kiya gaya hai */}
              {paginatedTypes.map((type) => (
                <tr
                  key={type.type_id}
                  className={selectedTypes.includes(type.type_id) ? 'types-selected-row' : ''}
                >
                  <td className="types-checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(type.type_id)}
                      onChange={() => handleSelectType(type.type_id)}
                    />
                  </td>
                  <td className="types-name-cell">
                    <span className="types-name">{type.type_name}</span>
                  </td>
                  <td className="types-shop-cell">
                    <span className="types-shop-badge">
                      {getShopDisplayName(type)}
                    </span>
                  </td>
                  <td className="types-actions-cell">
                    <button
                      type="button"
                      className="types-edit-btn"
                      onClick={() => handleOpenModalForEdit(type)}
                      title="Edit Type"
                    >
                      <FaEdit />
                    </button>
                    <button
                      type="button"
                      className="types-delete-btn"
                      onClick={() => handleDeleteSingleType(type)}
                      title="Delete Type"
                    >
                      <RiDeleteBin5Line />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ✅ 10 per page:  1-10 of N   < Previous   Page 1 of N   Next > */}
      <TablePagination
        page={page}
        totalItems={filteredTypes.length}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      {/* MODAL - Category field removed */}
      {isModalOpen && (
        <div className="types-modal-overlay" onClick={handleCloseModal}>
          <div className="types-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="types-modal-header">
              <h3>{editingType ? 'Edit Type' : 'Add New Type'}</h3>
              <button type="button" className="types-modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="types-modal-body">
              <form className="types-form" onSubmit={handleSave}>
                <div className="types-form-row">
                  <label className="types-form-label">SHOP:</label>
                  <select className="types-shop-select-dropdown" value={selectedShopId} onChange={handleModalShopChange}>
                    <option value="">-- Select a Shop --</option>
                    {shops.map((shop) => (
                      <option key={shop.shop_id} value={shop.shop_id}>
                        {shop.shop_name} (ID: {shop.shop_id})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="types-form-row">
                  <label className="types-form-label">TYPE NAME:</label>
                  <input
                    type="text"
                    value={typeName}
                    onChange={(e) => setTypeName(e.target.value)}
                    placeholder="Enter type name (e.g., Hex, Non-Hex)"
                    autoFocus
                    className="types-input"
                  />
                </div>

                <button type="submit" style={{ display: 'none' }} aria-hidden="true">Submit</button>
              </form>
            </div>
            <div className="types-modal-footer">
              <button type="button" className="types-btn-cancel" onClick={handleCloseModal}>Cancel</button>
              {editingType && (
                <button type="button" className="types-btn-delete" onClick={() => handleDeleteSingleType(editingType)}>Delete</button>
              )}

              {/* UPDATED SAVE BUTTON */}
              <button
                type="button"
                className="types-btn-save"
                onClick={handleSave}
                disabled={isSaving} // Button disable ho jayega jab tak save ho raha ho
              >
                {isSaving ? 'Saving...' : (editingType ? 'Update Type' : 'Save Type')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Types;